import {defineMessages, useIntl} from 'react-intl'
import {useEffect, useMemo, useState} from 'react'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'

import {notifyMe} from '../../../integrations/notifyme/hooks/use-notify-me'

const messages = defineMessages({
    notifyMeError: {
        defaultMessage: 'Failed to submit data',
        id: 'pdp_notify_me_general.error'
    }
})

const useQuickViewNotifyMe = ({
    product,
    variant,
    variationAttributes,
    variationParams,
    notifyMeEnabled,
    isVariationDataLoading,
    isNotifyMeModalStateOpen
}) => {
    const intl = useIntl()
    const {isLoading: isNotifyMeLoading, submitNotifyMe} = notifyMe()
    const {data: customer} = useCurrentCustomer()
    const [isNotifyMeModalOpen, setIsNotifyMeModalOpen] = useState(false)
    const [notifyMeSuccessContent, setNotifyMeSuccessContent] = useState(false)
    const [notifyMeError, setNotifyMeError] = useState(null)

    const isNotifyMe = useMemo(() => {
        if (isVariationDataLoading) {
            return false
        }
        if (!notifyMeEnabled) {
            return false
        }

        const lengthVariation = variationAttributes?.find((variation) => variation.id === 'length')
        const sizeVariation = variationAttributes?.find((variation) => variation.id === 'size')
        const selectedSize = sizeVariation?.selectedValue?.value
        const selectedLength = lengthVariation?.selectedValue?.value
        const hasLengthVariation = lengthVariation?.values?.length > 0
        let isNotifyMeFlag = false

        if (variant && selectedSize && !hasLengthVariation) {
            isNotifyMeFlag =
                notifyMeEnabled &&
                product?.c_notifyMeArray?.find(({size}) => size === selectedSize)?.size ===
                    selectedSize &&
                !variant?.orderable
        }

        if (variant && hasLengthVariation && selectedSize && selectedLength) {
            const notifyMeSelectedVariation = product?.c_notifyMeArray?.find(
                ({size, length}) => size === selectedSize && length === selectedLength
            )
            isNotifyMeFlag =
                notifyMeEnabled &&
                notifyMeSelectedVariation?.size === selectedSize &&
                notifyMeSelectedVariation?.length === selectedLength &&
                !variant?.orderable
        }

        return isNotifyMeFlag
    }, [variationAttributes, variant, product, notifyMeEnabled, isVariationDataLoading])

    const isFewItemsLeft = useMemo(() => {
        if (isVariationDataLoading) {
            return false
        }
        const selectedSize = variationAttributes?.find((variation) => variation.id === 'size')
            ?.selectedValue?.value
        const selectedLength = variationAttributes?.find((variation) => variation.id === 'length')
            ?.selectedValue?.value
        const hasLengthVariation = product?.variationAttributes?.find((attr) => attr.id === 'length')

        if (selectedSize) {
            const sizeFewItemsLeft =
                hasLengthVariation && selectedLength
                    ? product?.c_fewItemsLeftArray?.find(
                          ({size, length}) => size === selectedSize && length === selectedLength
                      )
                    : product?.c_fewItemsLeftArray?.find(({size}) => size === selectedSize)
            return !!sizeFewItemsLeft
        }
        return false
    }, [product, variationAttributes, isVariationDataLoading])

    // Handle notify me modal close
    const handleNotifyMeCloseModal = () => {
        setIsNotifyMeModalOpen(false)
        isNotifyMeModalStateOpen(false)
        setNotifyMeSuccessContent(false)
    }

    // Handle notify me functionality
    const handleNotifyMe = async () => {
        if (customer?.isRegistered) {
            const formData = {
                notifyMeEmail: customer?.email,
                notifyMeTermsAndConditions: false,
                notifyMeSKU: product?.id
            }
            const notifyMeResponse = await submitNotifyMe({formData: JSON.stringify(formData)})

            if (
                notifyMeResponse &&
                notifyMeResponse?.requestId &&
                notifyMeResponse?.resultMessages.length === 0
            ) {
                setNotifyMeSuccessContent(true)
                setIsNotifyMeModalOpen(true)
                isNotifyMeModalStateOpen(true)
            } else {
                setNotifyMeSuccessContent(false)
                setIsNotifyMeModalOpen(false)
                isNotifyMeModalStateOpen(false)
                setNotifyMeError(intl.formatMessage(messages.notifyMeError))
            }
        } else {
            setIsNotifyMeModalOpen(true)
            isNotifyMeModalStateOpen(true)
        }
    }

    // Notify me icon logic - aligned with PDP behavior
    const notifyMeIcon = (id, value, orderable) => {
        if (isVariationDataLoading) {
            return false
        }
        const hasSizeVariation = product?.variationAttributes?.find((attr) => attr.id === 'size')
        const hasLengthVariation = product?.variationAttributes?.find((attr) => attr.id === 'length')
        let showNotifyMeIcon = false

        if (hasSizeVariation && !hasLengthVariation) {
            const notifyMeVariations = product?.c_notifyMeArray?.find(({size}) => size === value)
            showNotifyMeIcon =
                notifyMeEnabled &&
                id === 'size' &&
                notifyMeVariations?.size === value &&
                !orderable
        }

        if (hasSizeVariation && hasLengthVariation) {
            // Use variationParams.length (user-selected) - same as PDP
            const selectedLength = variationParams?.length
            if (selectedLength) {
                // Find notify me entry for THIS size value + selected length
                const notifyMeVariations = product?.c_notifyMeArray?.find(
                    ({size, length}) => size === value && length === selectedLength
                )
                showNotifyMeIcon =
                    notifyMeEnabled &&
                    id === 'size' &&
                    notifyMeVariations?.size === value &&
                    notifyMeVariations?.length === selectedLength &&
                    !orderable
            }
        }

        return showNotifyMeIcon
    }

    return {
        isNotifyMe,
        isFewItemsLeft,
        isNotifyMeLoading,
        notifyMeError,
        isNotifyMeModalOpen,
        notifyMeSuccessContent,
        handleNotifyMe,
        handleNotifyMeCloseModal,
        notifyMeIcon
    }
}

export default useQuickViewNotifyMe
